import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Flag } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Modal para solicitar / EDITAR revisión de un activo.
// - Lista destinatarios agrupados por rol.
// - Nota obligatoria + Prioridad obligatoria (ALTA rojo / MEDIA amarillo / BAJA verde).
// - En modo EDIT (prop `mode="edit"`): PATCH sobre la solicitud activa; solo
//   visible al creador. Precarga destinatarios/nota/prioridad actuales.
const PRIORITY_OPTIONS = [
  { v: 'alta',  l: 'ALTA',  cls: 'bg-red-500 text-white border-red-600',      dot: 'bg-red-500' },
  { v: 'media', l: 'MEDIA', cls: 'bg-amber-400 text-slate-900 border-amber-500', dot: 'bg-amber-400' },
  { v: 'baja',  l: 'BAJA',  cls: 'bg-emerald-500 text-white border-emerald-600', dot: 'bg-emerald-500' },
];

const RequestReviewModal = ({ open, onClose, asset, onSuccess, mode = 'create', initial = null }) => {
  const isEdit = mode === 'edit';
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [note, setNote] = useState('');
  const [priority, setPriority] = useState('media');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  // (Feb 2026) Plazos configurados en días — sirven para mostrar "Vence en X días"
  // al elegir la prioridad. Si el usuario no tiene permiso para leer los settings
  // caemos silenciosamente a los defaults 3/5/7.
  const [deadlineDays, setDeadlineDays] = useState({ alta: 3, media: 5, baja: 7 });

  useEffect(() => {
    if (!open || !asset?.id) return;
    setLoading(true);
    setNote(isEdit && initial?.note ? initial.note : '');
    setPriority(isEdit && initial?.priority ? initial.priority : 'media');
    setSelected(new Set(isEdit && Array.isArray(initial?.recipient_ids) ? initial.recipient_ids : []));
    setLoadError(null);
    setDebugInfo(null);
    axios
      .get(`${API}/settings/review-deadlines`, { withCredentials: true })
      .then((r) => {
        setDeadlineDays({
          alta: Number(r.data?.alta_days ?? 3),
          media: Number(r.data?.media_days ?? 5),
          baja: Number(r.data?.baja_days ?? 7),
        });
      })
      .catch(() => {});
    axios
      .get(`${API}/assets/${asset.id}/review-recipients`, { withCredentials: true })
      .then((r) => {
        const g = r.data?.groups || [];
        setGroups(g);
        setDebugInfo(r.data?._debug || null);
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail || err?.message || 'Error al cargar destinatarios';
        setLoadError(`${err?.response?.status || 'ERR'}: ${detail}`);
        toast.error(detail);
      })
      .finally(() => setLoading(false));
  }, [open, asset?.id, isEdit, initial?.note, initial?.priority, initial?.recipient_ids]);

  if (!open) return null;

  const toggleUser = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const toggleGroup = (group) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = group.users.every((u) => next.has(u.id));
      group.users.forEach((u) => {
        if (allSelected) next.delete(u.id);
        else next.add(u.id);
      });
      return next;
    });
  };

  const canSubmit = selected.size > 0 && note.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        recipient_ids: Array.from(selected),
        note: note.trim(),
        priority,
      };
      if (isEdit) {
        await axios.patch(`${API}/assets/${asset.id}/review-request`, payload, { withCredentials: true });
        toast.success('Solicitud de revisión actualizada');
        onSuccess?.();
      } else {
        const r = await axios.post(
          `${API}/assets/${asset.id}/toggle-review`,
          { marked: true, ...payload },
          { withCredentials: true },
        );
        toast.success('Solicitud de revisión enviada');
        onSuccess?.(r.data?.asset);
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="request-review-modal-overlay"
    >
      <div
        className="rounded-xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col"
        style={{ background: 'var(--color-bg-elevated)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="request-review-modal"
      >
        <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-500 text-white">
              <Flag className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base" style={{ color: 'var(--color-text-primary)' }}>
                {isEdit ? 'Editar solicitud de revisión' : 'Solicitar revisión'}
              </h3>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                {isEdit ? 'Modifica destinatarios, nota o prioridad' : 'Escoge los destinatarios y describe el motivo'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-700/20 rounded"
            aria-label="Cerrar"
            data-testid="request-review-close"
          >
            <X className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
            </div>
          ) : (
            <>
              <div>
                <p className="text-xs uppercase tracking-wide font-semibold mb-2" style={{ color: 'var(--color-text-muted)' }}>
                  Destinatarios
                </p>
                <div className="space-y-3">
                  {groups.map((group) => {
                    if (!group.users.length) return null;
                    const allSelected = group.users.every((u) => selected.has(u.id));
                    return (
                      <div
                        key={group.role}
                        className="border rounded-lg p-3"
                        style={{ borderColor: 'var(--color-border)' }}
                        data-testid={`review-group-${group.role}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-primary)' }}>
                            {group.label} · {group.users.length}
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleGroup(group)}
                            className="text-[10px] font-medium text-amber-600 hover:text-amber-700"
                            data-testid={`review-group-toggle-${group.role}`}
                          >
                            {allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                          </button>
                        </div>
                        <div className="space-y-1">
                          {group.users.map((u) => (
                            <label
                              key={u.id}
                              className="flex items-center gap-2 text-sm cursor-pointer hover:opacity-90"
                              data-testid={`review-user-${u.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={selected.has(u.id)}
                                onChange={() => toggleUser(u.id)}
                                className="w-4 h-4 accent-amber-500"
                              />
                              <span style={{ color: 'var(--color-text-primary)' }}>{u.name}</span>
                              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                ({u.role})
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {groups.every((g) => !g.users.length) && !loadError && (
                    <div className="text-sm space-y-2">
                      <p className="italic" style={{ color: 'var(--color-text-muted)' }}>
                        No hay destinatarios disponibles para este activo.
                      </p>
                      {debugInfo && (
                        <details className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
                          <summary className="cursor-pointer hover:underline">Diagnóstico</summary>
                          <pre className="mt-1 whitespace-pre-wrap font-mono">
                            {JSON.stringify(debugInfo, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                  {loadError && (
                    <div className="text-sm p-3 rounded bg-red-50 border border-red-200 text-red-700">
                      <p className="font-semibold mb-1">No se pudieron cargar los destinatarios</p>
                      <p className="text-xs font-mono">{loadError}</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label
                  className="text-xs uppercase tracking-wide font-semibold mb-2 flex items-center gap-2"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Prioridad <span className="text-red-500">*</span>
                </label>
                <div className="inline-flex gap-2" role="tablist" data-testid="request-review-priority">
                  {PRIORITY_OPTIONS.map((opt) => {
                    const active = priority === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setPriority(opt.v)}
                        className={`px-3 py-1.5 text-xs font-bold tracking-wide rounded border transition-all ${
                          active ? opt.cls + ' shadow-sm' : 'border-slate-500/50 text-slate-400 hover:text-slate-200 hover:border-slate-400'
                        }`}
                        data-testid={`request-review-priority-${opt.v}`}
                        aria-pressed={active}
                      >
                        {opt.l}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }} data-testid="request-review-priority-hint">
                  Plazo asignado: <span className="font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
                    {deadlineDays[priority] ?? 5} {(deadlineDays[priority] ?? 5) === 1 ? 'día' : 'días'}
                  </span> {isEdit ? '· Se recalcula desde la fecha original de creación' : 'desde la creación'}
                </p>
              </div>

              <div>
                <label
                  className="text-xs uppercase tracking-wide font-semibold mb-2 flex items-center gap-2"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Nota <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                  placeholder="Describe el motivo de la revisión…"
                  className="w-full text-sm rounded-md border p-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  style={{
                    background: 'var(--color-bg)',
                    color: 'var(--color-text-primary)',
                    borderColor: 'var(--color-border)',
                  }}
                  data-testid="request-review-note"
                />
                {note.trim().length === 0 && (
                  <p className="text-[11px] text-red-500 mt-1">La nota es obligatoria.</p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded border"
            style={{
              color: 'var(--color-text-primary)',
              borderColor: 'var(--color-border)',
              background: 'transparent',
            }}
            data-testid="request-review-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`px-4 py-2 text-sm font-semibold rounded flex items-center gap-2 transition-colors ${
              canSubmit
                ? 'bg-amber-500 hover:bg-amber-600 text-white'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
            data-testid="request-review-submit"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Guardar cambios' : 'Enviar solicitud de revisión'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestReviewModal;
